import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemFieldsAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';

import type { colorField_updateWorkItemFieldsActionRenderer$key } from './__generated__/colorField_updateWorkItemFieldsActionRenderer.graphql';

const messages = defineMessages({
	updateWorkItemColor: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.jira-color-field.update-work-item-color',
		defaultMessage: 'Update work item color to {fieldValue}',
		description: 'Label for the change action in the jira color field',
	},
	clearAction: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.jira-color-field.clear-action',
		defaultMessage: 'Clear {fieldName}',
		description: 'Label for the clear action in the jira color field',
	},
});

export type Props = {
	queryRef: colorField_updateWorkItemFieldsActionRenderer$key;
	action: UpdateWorkItemFieldsAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const ColorField = ({
	queryRef,
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment colorField_updateWorkItemFieldsActionRenderer on JiraColorField {
				fieldId
				name
				color {
					colorKey
				}
			}
		`,
		queryRef,
	);

	const getColorLabel = (colorKey: string): string =>
		colorKey
			.toLowerCase()
			.replace(/_/g, ' ')
			.replace(/^\w/, (char) => char.toUpperCase());

	const isFieldRenderable = data?.fieldId === action?.data?.fieldKey;
	const isUpdatingAction = Boolean(action?.data?.fieldValue);

	const currentColorLabel = data?.color?.colorKey ? getColorLabel(data.color.colorKey) : undefined;
	const suggestedColorLabel = action?.data?.fieldValue
		? getColorLabel(String(action.data.fieldValue))
		: undefined;
	const colorLabel = suggestedColorLabel || '';

	const toolTipContentProvider = useCallback(
		() => (
			<FieldTooltipContent
				reasoning={action?.data?.reasoning}
				fieldName={data?.name}
				current={currentColorLabel}
				suggested={suggestedColorLabel}
			/>
		),
		[action?.data?.reasoning, data?.name, currentColorLabel, suggestedColorLabel],
	);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: action?.data?.fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	if (!data?.fieldId || !data?.name || !isFieldRenderable) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-fields-action-renderer.ui.action-card.card-fields-list.jira-color-field"
		>
			{formatMessage(isUpdatingAction ? messages.updateWorkItemColor : messages.clearAction, {
				fieldName: data?.name?.toLowerCase(),
				fieldValue: colorLabel,
			})}
		</Text>
	);
};
